// =============================================================================
// Job Invoice (Bill) editor — faithful port of IMS Invoice_Add (create + edit).
// Customer picker (with snapshot), billed line-item grid with product search,
// per-type + whole-invoice discounts, tax, deposit, live totals. Replaces only
// the billed items on save; actual-cost rows and labor hours are preserved.
// =============================================================================
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2, Search, Save, X } from 'lucide-react'
import { toast } from 'react-toastify'
import {
  getBillWithItems, saveBill, getNextBillNumber, getDefaultTaxRate,
  searchProducts, listCustomers,
} from '../services/data'
import { PageHeader, Card, Spinner, Button } from '../components/shared/ui'
import { currency, customerLabel } from '../lib/format'
import {
  ESTIMATE_TYPES, computeEstimateTotals, lineTotal, typeBucket, type TypeDiscounts,
} from '../lib/estimateTotals'
import type { Customer, Product, Bill, BillItem } from '../types'

const TYPE_OPTIONS = [...ESTIMATE_TYPES, 'Hidden Cost'] as const

interface EditItem {
  key: string
  legacy_inventory_code: number | null
  item_name: string
  item_size: string | null
  item_type: string
  qty: number
  price: number
  cost: number
  discount_percent: number
  discount_dollar: number
}

let counter = 0
const uid = () => `brow-${Date.now()}-${counter++}`
const num = (v: string) => (v === '' ? 0 : parseFloat(v) || 0)

function mapType(inv: string | null): string {
  if (!inv) return 'Materials'
  const hit = TYPE_OPTIONS.find(t => t.toLowerCase() === inv.toLowerCase())
  return hit ?? 'Plants'
}

function productToItem(p: Product): EditItem {
  return {
    key: uid(),
    legacy_inventory_code: p.legacy_ims_inventory_code ?? p.legacy_pos_inventory_code ?? null,
    item_name: p.name,
    item_size: p.size ?? null,
    item_type: mapType(p.inventory_type),
    qty: 1,
    price: p.retail_price ?? p.sale_price ?? 0,
    cost: p.cost_price ?? 0,
    discount_percent: 0,
    discount_dollar: 0,
  }
}

function itemFromBill(it: BillItem): EditItem {
  return {
    key: uid(),
    legacy_inventory_code: it.legacy_inventory_code,
    item_name: it.item_name ?? '',
    item_size: it.item_size ?? null,
    item_type: it.item_type ?? 'Materials',
    qty: it.qty ?? 0,
    price: it.price ?? 0,
    cost: it.cost ?? 0,
    discount_percent: it.discount_percent ?? 0,
    discount_dollar: it.discount_dollar ?? 0,
  }
}

export default function BillEditor() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [existing, setExisting] = useState<Bill | null>(null)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [items, setItems] = useState<EditItem[]>([])

  const [billDate, setBillDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [comments, setComments] = useState('')
  const [depositInfo, setDepositInfo] = useState('')
  const [isNonTaxable, setIsNonTaxable] = useState(false)
  const [taxPercent, setTaxPercent] = useState(0)
  const [deposit, setDeposit] = useState(0)
  const [wholeP, setWholeP] = useState(0)
  const [wholeD, setWholeD] = useState(0)
  const [discounts, setDiscounts] = useState<TypeDiscounts>({
    plant_p: 0, plant_d: 0, material_p: 0, material_d: 0,
    labor_p: 0, labor_d: 0, rental_p: 0, rental_d: 0,
  })

  useEffect(() => {
    let active = true
    ;(async () => {
      setLoading(true)
      try {
        if (isEdit && id) {
          const res = await getBillWithItems(id)
          if (res && active) {
            const b = res.bill
            setExisting(b)
            setCustomer(res.customer)
            setItems(res.items.filter(i => !i.is_actual).map(itemFromBill))
            setBillDate((b.bill_date ?? new Date().toISOString()).slice(0, 10))
            setComments(b.comments ?? b.comment ?? '')
            setDepositInfo(b.deposit_info ?? '')
            setIsNonTaxable(Boolean(b.is_non_taxable))
            setTaxPercent(b.sales_tax_percent ?? 0)
            setDeposit(b.deposit ?? 0)
            setWholeP(b.whole_invoice_discount_p ?? 0)
            setWholeD(b.whole_invoice_discount_d ?? 0)
            setDiscounts({
              plant_p: b.inv_plant_discount_p ?? 0, plant_d: b.inv_plant_discount_d ?? 0,
              material_p: b.inv_material_discount_p ?? 0, material_d: b.inv_material_discount_d ?? 0,
              labor_p: b.inv_labor_discount_p ?? 0, labor_d: b.inv_labor_discount_d ?? 0,
              rental_p: b.rental_discount_percent ?? 0, rental_d: b.rental_discount ?? 0,
            })
          }
        } else {
          const rate = await getDefaultTaxRate().catch(() => 0)
          if (active) setTaxPercent(rate)
        }
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => { active = false }
  }, [id, isEdit])

  const totals = useMemo(
    () => computeEstimateTotals({ items, discounts, wholeDiscountP: wholeP, wholeDiscountD: wholeD, taxPercent, isNonTaxable, deposit }),
    [items, discounts, wholeP, wholeD, taxPercent, isNonTaxable, deposit],
  )

  const updateItem = (key: string, patch: Partial<EditItem>) =>
    setItems(prev => prev.map(i => (i.key === key ? { ...i, ...patch } : i)))
  const removeItem = (key: string) => setItems(prev => prev.filter(i => i.key !== key))
  const addBlank = () =>
    setItems(prev => [...prev, {
      key: uid(), legacy_inventory_code: null, item_name: '', item_size: null,
      item_type: 'Plants', qty: 1, price: 0, cost: 0, discount_percent: 0, discount_dollar: 0,
    }])

  const save = async () => {
    if (!customer) { toast.error('Select a customer first'); return }
    if (items.length === 0) { toast.error('Add at least one line item'); return }
    setSaving(true)
    try {
      const t = totals
      const billCode = existing?.bill_code ?? String(await getNextBillNumber())
      const legacyId = existing?.legacy_bill_id ?? (isEdit ? null : await getNextBillNumber())

      const payload: Partial<Bill> = {
        bill_code: billCode,
        legacy_bill_id: legacyId,
        customer_id: customer.id,
        legacy_customer_id: (customer.legacy_pos_id ?? customer.legacy_ims_id ?? null) as number | null,
        bill_date: new Date(billDate).toISOString(),
        is_non_taxable: isNonTaxable,
        comments, comment: comments, deposit_info: depositInfo,
        // Customer snapshot (Invoice_report falls back to these).
        customer_name: customerLabel(customer),
        customer_address: customer.address ?? null,
        customer_city: customer.city ?? null,
        customer_state: customer.state ?? null,
        customer_zip: customer.zip ?? null,
        customer_phone1: customer.phone_home ?? null,
        customer_phone2: customer.phone_work ?? null,
        inv_plant_discount_p: discounts.plant_p, inv_plant_discount_d: discounts.plant_d,
        inv_material_discount_p: discounts.material_p, inv_material_discount_d: discounts.material_d,
        inv_labor_discount_p: discounts.labor_p, inv_labor_discount_d: discounts.labor_d,
        rental_discount_percent: discounts.rental_p, rental_discount: discounts.rental_d,
        whole_invoice_discount_p: wholeP, whole_invoice_discount_d: wholeD,
        plant_total: t.typeGross['Plants'] ?? 0, disc_plant_total: t.typeDiscounted['Plants'] ?? 0,
        material_total: t.typeGross['Materials'] ?? 0, disc_material_total: t.typeDiscounted['Materials'] ?? 0,
        labor_total: t.typeGross['Labor'] ?? 0, disc_labor_total: t.typeDiscounted['Labor'] ?? 0,
        rental_total: t.typeGross['Rental'] ?? 0, disc_rental_total: t.typeDiscounted['Rental'] ?? 0,
        invoice_sub_total: t.subtotal, whole_invoice: t.subtotal, disc_whole_invoice: t.wholeDiscounted,
        sales_tax_percent: taxPercent, sales_tax_amount: t.taxAmount,
        invoice_total: t.total, remaining_balance: t.remaining, deposit,
      }

      const itemRows: Partial<BillItem>[] = items.map(i => ({
        legacy_inventory_code: i.legacy_inventory_code,
        item_name: i.item_name, item_size: i.item_size, item_type: i.item_type,
        qty: i.qty, price: i.price, cost: i.cost,
        discount_percent: i.discount_percent, discount_dollar: i.discount_dollar,
        total: lineTotal(i), is_committed: true,
      }))

      const saved = await saveBill({ id, bill: payload, items: itemRows })
      toast.success(isEdit ? 'Job invoice updated' : 'Job invoice created')
      navigate(`/bills/${saved.id}`)
    } catch (e) {
      console.error(e)
      toast.error('Failed to save job invoice')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Spinner label="Loading job invoice…" />

  return (
    <div className="pb-24">
      <Button variant="ghost" onClick={() => navigate(isEdit && id ? `/bills/${id}` : '/bills')} className="mb-4">
        <ArrowLeft size={16} /> Cancel
      </Button>
      <PageHeader
        title={isEdit ? `Edit Job Invoice ${existing?.bill_code ?? ''}` : 'New Job Invoice'}
        subtitle="Faithful port of IMS Invoice_Add"
        actions={<Button disabled={saving} onClick={save}><Save size={16} /> {saving ? 'Saving…' : 'Save Job Invoice'}</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Card className="p-4 lg:col-span-2">
          <CustomerPicker customer={customer} onSelect={setCustomer} />
        </Card>
        <Card className="p-4 space-y-2">
          <label className="block text-sm">
            <span className="text-gray-600">Invoice Date</span>
            <input type="date" value={billDate} onChange={e => setBillDate(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" />
          </label>
          <label className="flex items-center gap-2 text-sm mt-2">
            <input type="checkbox" checked={isNonTaxable} onChange={e => setIsNonTaxable(e.target.checked)} />
            <span className="text-gray-700">Non-taxable</span>
          </label>
        </Card>
      </div>

      <Card className="p-4 mb-4">
        <ItemSearch onPick={p => setItems(prev => [...prev, productToItem(p)])} />
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-2 w-32">Type</th>
                <th className="py-2 pr-2">Description</th>
                <th className="py-2 pr-2 w-24">Size</th>
                <th className="py-2 pr-2 w-16 text-right">Qty</th>
                <th className="py-2 pr-2 w-28 text-right">Price</th>
                <th className="py-2 pr-2 w-20 text-right">Disc %</th>
                <th className="py-2 pr-2 w-24 text-right">Disc $</th>
                <th className="py-2 pr-2 w-28 text-right">Total</th>
                <th className="py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={9} className="py-6 text-center text-gray-400">No line items. Search a product above or add a blank row.</td></tr>
              ) : items.map(i => (
                <tr key={i.key} className="border-b border-gray-100">
                  <td className="py-1 pr-2">
                    <select value={i.item_type} onChange={e => updateItem(i.key, { item_type: e.target.value })}
                      className="w-full px-2 py-1 rounded border border-gray-300">
                      {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-2">
                    <input value={i.item_name} onChange={e => updateItem(i.key, { item_name: e.target.value })}
                      className="w-full px-2 py-1 rounded border border-gray-300" />
                  </td>
                  <td className="py-1 pr-2">
                    <input value={i.item_size ?? ''} onChange={e => updateItem(i.key, { item_size: e.target.value })}
                      className="w-full px-2 py-1 rounded border border-gray-300" />
                  </td>
                  <td className="py-1 pr-2">
                    <input type="number" value={i.qty} onChange={e => updateItem(i.key, { qty: num(e.target.value) })}
                      className="w-full px-2 py-1 rounded border border-gray-300 text-right" />
                  </td>
                  <td className="py-1 pr-2">
                    <input type="number" step="0.01" value={i.price} onChange={e => updateItem(i.key, { price: num(e.target.value) })}
                      className="w-full px-2 py-1 rounded border border-gray-300 text-right" />
                  </td>
                  <td className="py-1 pr-2">
                    <input type="number" step="0.01" value={i.discount_percent} onChange={e => updateItem(i.key, { discount_percent: num(e.target.value) })}
                      className="w-full px-2 py-1 rounded border border-gray-300 text-right" />
                  </td>
                  <td className="py-1 pr-2">
                    <input type="number" step="0.01" value={i.discount_dollar} onChange={e => updateItem(i.key, { discount_dollar: num(e.target.value) })}
                      className="w-full px-2 py-1 rounded border border-gray-300 text-right" />
                  </td>
                  <td className="py-1 pr-2 text-right font-medium">{currency(lineTotal(i))}</td>
                  <td className="py-1 text-right">
                    <button onClick={() => removeItem(i.key)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={addBlank} className="mt-3 text-sm text-green-700 hover:underline flex items-center gap-1">
          <Plus size={14} /> Add blank row
        </button>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2">
          <h3 className="font-semibold text-gray-800 mb-3">Discounts</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <DiscPair label="Plants" p={discounts.plant_p} d={discounts.plant_d}
              onP={v => setDiscounts(s => ({ ...s, plant_p: v }))} onD={v => setDiscounts(s => ({ ...s, plant_d: v }))} />
            <DiscPair label="Materials" p={discounts.material_p} d={discounts.material_d}
              onP={v => setDiscounts(s => ({ ...s, material_p: v }))} onD={v => setDiscounts(s => ({ ...s, material_d: v }))} />
            <DiscPair label="Labor" p={discounts.labor_p} d={discounts.labor_d}
              onP={v => setDiscounts(s => ({ ...s, labor_p: v }))} onD={v => setDiscounts(s => ({ ...s, labor_d: v }))} />
            <DiscPair label="Rental" p={discounts.rental_p} d={discounts.rental_d}
              onP={v => setDiscounts(s => ({ ...s, rental_p: v }))} onD={v => setDiscounts(s => ({ ...s, rental_d: v }))} />
            <DiscPair label="Whole Invoice" p={wholeP} d={wholeD} onP={setWholeP} onD={setWholeD} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <label className="block text-sm">
              <span className="text-gray-600">Comments</span>
              <textarea value={comments} onChange={e => setComments(e.target.value)} rows={2}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" />
            </label>
            <label className="block text-sm">
              <span className="text-gray-600">Deposit Info</span>
              <textarea value={depositInfo} onChange={e => setDepositInfo(e.target.value)} rows={2}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" />
            </label>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Totals</h3>
          <Row label="Subtotal" value={currency(totals.subtotal)} />
          {(wholeP > 0 || wholeD > 0) && <Row label="After Whole Discount" value={currency(totals.wholeDiscounted)} />}
          <div className="flex items-center justify-between py-1 text-sm">
            <span className="text-gray-600">Sales Tax</span>
            <div className="flex items-center gap-1">
              <input type="number" step="0.01" value={taxPercent} disabled={isNonTaxable}
                onChange={e => setTaxPercent(num(e.target.value))}
                className="w-16 px-2 py-1 rounded border border-gray-300 text-right disabled:bg-gray-100" />
              <span className="text-gray-400">%</span>
              <span className="w-20 text-right font-medium">{currency(totals.taxAmount)}</span>
            </div>
          </div>
          <Row label="Invoice Total" value={currency(totals.total)} bold />
          <div className="flex items-center justify-between py-1 text-sm">
            <span className="text-gray-600">Deposit</span>
            <input type="number" step="0.01" value={deposit} onChange={e => setDeposit(num(e.target.value))}
              className="w-28 px-2 py-1 rounded border border-gray-300 text-right" />
          </div>
          <Row label="Remaining Balance" value={currency(totals.remaining)} bold />
          {isEdit && (
            <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
              Worker hours &amp; overhead (job-costing) are preserved from the existing invoice.
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between py-1 ${bold ? 'text-base font-bold text-gray-900 border-t border-gray-200 mt-1 pt-2' : 'text-sm text-gray-600'}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  )
}

function DiscPair({ label, p, d, onP, onD }: { label: string; p: number; d: number; onP: (v: number) => void; onD: (v: number) => void }) {
  return (
    <div>
      <div className="text-gray-600 mb-1">{label}</div>
      <div className="flex items-center gap-1">
        <input type="number" step="0.01" value={p} onChange={e => onP(num(e.target.value))}
          className="w-full px-2 py-1 rounded border border-gray-300 text-right" placeholder="%" />
        <input type="number" step="0.01" value={d} onChange={e => onD(num(e.target.value))}
          className="w-full px-2 py-1 rounded border border-gray-300 text-right" placeholder="$" />
      </div>
    </div>
  )
}

function CustomerPicker({ customer, onSelect }: { customer: Customer | null; onSelect: (c: Customer | null) => void }) {
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<Customer[]>([])
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!term.trim()) { setResults([]); return }
    const t = setTimeout(async () => {
      try {
        const { rows } = await listCustomers({ search: term, page: 0, pageSize: 8 })
        setResults(rows); setOpen(true)
      } catch { /* ignore */ }
    }, 250)
    return () => clearTimeout(t)
  }, [term])

  if (customer) {
    return (
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-400">Customer</div>
          <div className="font-semibold text-gray-900">{customerLabel(customer)}</div>
          {customer.address && <div className="text-sm text-gray-600">{[customer.address, customer.city, customer.state, customer.zip].filter(Boolean).join(', ')}</div>}
          {(customer.phone_home || customer.phone_work) && <div className="text-sm text-gray-600">{customer.phone_home ?? customer.phone_work}</div>}
        </div>
        <button onClick={() => onSelect(null)} className="text-gray-400 hover:text-red-600 p-1"><X size={16} /></button>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Customer</div>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={term} onChange={e => setTerm(e.target.value)} placeholder="Search customers by name, company, phone…"
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {results.map(c => (
            <button key={c.id} onClick={() => { onSelect(c); setOpen(false); setTerm('') }}
              className="block w-full text-left px-3 py-2 hover:bg-green-50 text-sm border-b border-gray-50 last:border-0">
              <div className="font-medium text-gray-800">{customerLabel(c)}</div>
              <div className="text-xs text-gray-500">{[c.city, c.state].filter(Boolean).join(', ')}{c.phone_home ? ` · ${c.phone_home}` : ''}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ItemSearch({ onPick }: { onPick: (p: Product) => void }) {
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<Product[]>([])
  const [open, setOpen] = useState(false)

  const run = useCallback(async (t: string) => {
    if (!t.trim()) { setResults([]); return }
    try { setResults(await searchProducts(t, 12)); setOpen(true) } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    const h = setTimeout(() => run(term), 250)
    return () => clearTimeout(h)
  }, [term, run])

  return (
    <div className="relative">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={term} onChange={e => setTerm(e.target.value)} placeholder="Search products to add by name, SKU, or code…"
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {results.map(p => (
            <button key={p.id} onClick={() => { onPick(p); setOpen(false); setTerm('') }}
              className="flex justify-between w-full text-left px-3 py-2 hover:bg-green-50 text-sm border-b border-gray-50 last:border-0">
              <span className="text-gray-800">{p.name}{p.size ? ` — ${p.size}` : ''} <span className="text-xs text-gray-400">({typeBucket(mapType(p.inventory_type))})</span></span>
              <span className="text-green-700 font-medium">{currency(p.retail_price ?? p.sale_price ?? 0)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
